import type { Participant } from '@seen/core/models/patients/participant'
import { type JSX, Suspense } from 'react'
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary'
import { Outlet } from 'react-router'
import { ErrorFallback } from '#src/components/app/ErrorFallback.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { Helmet } from '#src/components/Helmet.tsx'
import { BasePageLayout } from '#src/components/layouts/BasePageLayout.tsx'
import { ParticipantRoomProvider } from '#src/components/ParticipantRoomProvider.tsx'
import { PatientDetailContent } from '#src/components/patient/detail-content/PatientDetailContent.tsx'
import { PatientDetail } from '#src/components/patient/PatientDetail.tsx'
import type { PatientNavigationItem } from '#src/hooks/usePatientNavigationItems.ts'

interface ParticipantPageLayoutProps {
  participant: Participant
  navigationItems: PatientNavigationItem[]
  outletContext?: unknown
}

export function ParticipantPageLayout({
  participant,
  navigationItems,
  outletContext,
}: ParticipantPageLayoutProps): JSX.Element {
  return (
    <ParticipantRoomProvider participantId={participant.id}>
      <BasePageLayout
        entity={participant}
        navigationItems={navigationItems}
        detail={
          <PatientDetail
            participant={participant}
            scrollRestorationIdPrefix="page-layout-detail"
          />
        }
        detailContent={(header) => (
          <PatientDetailContent participant={participant} header={header} />
        )}
      >
        <Helmet title={participant.name} />
        <ErrorBoundary
          fallbackRender={({ error, resetErrorBoundary }: FallbackProps) => (
            <ErrorFallback error={error} resetError={resetErrorBoundary} />
          )}
        >
          <Suspense fallback={<Loader />}>
            <Outlet context={outletContext} />
          </Suspense>
        </ErrorBoundary>
      </BasePageLayout>
    </ParticipantRoomProvider>
  )
}
